import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles'
import type { UiPalette } from '@theme/uiPalette.types'

export const createAppTheme = (p: UiPalette) =>
  createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: p.primary.main,
        dark: p.primary.dark,
        light: p.primary.light,
        contrastText: '#ffffff',
      },
      secondary: {
        main: p.secondary.main,
        dark: p.secondary.dark,
        light: p.secondary.light,
        contrastText: '#ffffff',
      },
      error: { main: p.status.error },
      warning: { main: p.status.warning },
      info: { main: p.status.info },
      success: { main: p.status.success },
      text: {
        primary: p.neutral.text,
        secondary: p.neutral.textMuted,
      },
      background: {
        default: p.neutral.background,
        paper: '#ffffff',
      },
      divider: p.neutral.borderMedium,
    },
    typography: {
      fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 14,
      htmlFontSize: 16,
      h1: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.3 },
      h2: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.32 },
      h3: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
      h4: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.38 },
      h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 },
      body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
      body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.45 },
      subtitle1: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.45 },
      caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 },
      overline: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1.5,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.2,
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            color: p.neutral.text,
            backgroundColor: p.neutral.background,
            fontSize: '0.875rem',
            lineHeight: 1.5,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: p.shell.headerShadow,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: p.neutral.borderMedium,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            verticalAlign: 'middle',
          },
          head: {
            fontWeight: 600,
            color: p.table.headText,
            fontSize: '0.8125rem',
            backgroundColor: p.table.headBg,
          },
          body: {
            fontSize: '0.875rem',
            color: p.neutral.text,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
          shrink: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            fontWeight: 500,
            minHeight: 42,
            textTransform: 'none',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.0625rem',
            fontWeight: 600,
            lineHeight: 1.35,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: p.table.rowHoverBg,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.875rem',
            padding: '8px 16px',
            minHeight: 36,
            boxShadow: 'none',
          },
          sizeSmall: {
            fontSize: '0.8125rem',
            padding: '6px 12px',
            minHeight: 32,
          },
          sizeLarge: {
            fontSize: '0.9375rem',
            padding: '10px 20px',
            minHeight: 42,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          outlined: {
            backgroundColor: '#ffffff',
            borderWidth: 1,
          },
          outlinedPrimary: {
            color: p.primary.dark,
            border: `1px solid ${p.primary.main}`,
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: alpha(p.primary.main, 0.06),
              border: `1px solid ${p.primary.dark}`,
              color: p.primary.dark,
            },
          },
          outlinedSecondary: {
            color: p.neutral.text,
            border: `1px solid ${p.neutral.outline}`,
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#fafafa',
              borderColor: '#bfbfbf',
              color: p.neutral.text,
            },
          },
          outlinedInherit: {
            color: p.neutral.text,
            border: `1px solid ${p.neutral.outline}`,
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#fafafa',
              borderColor: '#bfbfbf',
            },
          },
          textPrimary: {
            '&:hover': {
              backgroundColor: alpha(p.primary.main, 0.06),
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            '&.Mui-selected': {
              color: p.primary.dark,
              backgroundColor: p.primary.surface,
              fontWeight: 700,
              borderColor: `${alpha(p.primary.main, 0.45)}`,
            },
            '&.Mui-selected:hover': {
              backgroundColor: alpha(p.primary.main, 0.12),
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: '#ffffff',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: p.neutral.outline,
              borderWidth: 1,
            },
            '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: '#bfbfbf',
            },
            '&.Mui-focused:not(.Mui-error)': {
              backgroundColor: '#ffffff',
              boxShadow: `0 0 0 2px ${alpha(p.primary.main, 0.14)}`,
            },
            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
              borderColor: p.primary.main,
            },
            '&.Mui-disabled': {
              backgroundColor: '#ffffff',
            },
          },
          input: {
            padding: '8.5px 14px',
            backgroundColor: 'transparent',
            '&.Mui-disabled': {
              WebkitTextFillColor: p.neutral.textMuted ?? 'rgba(0, 0, 0, 0.55)',
              backgroundColor: 'transparent',
            },
          },
          inputSizeSmall: {
            padding: '6.5px 12px',
          },
          multiline: {
            backgroundColor: '#ffffff',
            '&.Mui-disabled': {
              backgroundColor: '#ffffff',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '&.Mui-disabled': {
                backgroundColor: '#ffffff',
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    uiPalette: p,
  } as ThemeOptions & { uiPalette: UiPalette })
